/**
 * Invitation Accept Page — Public route at /invite/:token
 *
 * Flow:
 *   1. Fetch invitation details via GET /api/v1/impact-core/invite/{token}
 *   2. Display: inviter name, organization, role, personal message
 *   3. On accept: POST /api/v1/impact-core/invite/{token}/accept
 *   4. Redirect to /login or / (dashboard) if already authenticated
 */

import { useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import {
  UserPlus,
  Loader2,
  AlertCircle,
  CheckCircle,
  Building2,
  Mail,
  Shield,
  Clock,
} from 'lucide-react';
import { apiClient } from '@/api/client';

// ─── Types ──────────────────────────────────────────────────────

interface InvitationDetails {
  token: string;
  inviter_name: string;
  inviter_email?: string;
  organization_name: string;
  role: string;
  role_description?: string;
  message?: string;
  expires_at?: string;
  status: 'pending' | 'accepted' | 'expired' | 'revoked';
}

// ─── Main Component ─────────────────────────────────────────────

export function InvitationAcceptPage() {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const [acceptError, setAcceptError] = useState<string | null>(null);

  // Fetch invitation details
  const invitationQuery = useQuery<InvitationDetails>({
    queryKey: ['ic', 'invitation', token],
    queryFn: async () => {
      const res = await apiClient.get(`/api/v1/impact-core/invite/${token}`);
      return res.data?.data ?? res.data;
    },
    staleTime: 120_000,
    enabled: !!token,
    retry: false,
  });

  // Accept mutation
  const acceptMutation = useMutation({
    mutationFn: async () => {
      const res = await apiClient.post(`/api/v1/impact-core/invite/${token}/accept`);
      return res.data?.data ?? res.data;
    },
    onSuccess: () => {
      // Short delay then redirect
      setTimeout(() => navigate('/login'), 2000);
    },
    onError: (error: unknown) => {
      const message =
        error instanceof Error ? error.message : 'Failed to accept invitation. Please try again.';
      setAcceptError(message);
    },
  });

  const handleAccept = useCallback(() => {
    setAcceptError(null);
    acceptMutation.mutate();
  }, [acceptMutation]);

  // ─── Token Missing ────────────────────────────────────────────

  if (!token) {
    return (
      <InvitationShell>
        <ErrorCard
          title="Invalid Invitation"
          message="No invitation token provided. Please check your invitation link."
        />
      </InvitationShell>
    );
  }

  // ─── Loading ──────────────────────────────────────────────────

  if (invitationQuery.isLoading) {
    return (
      <InvitationShell>
        <div className="flex flex-col items-center gap-3 py-12">
          <Loader2 className="h-6 w-6 animate-spin text-ic-accent" />
          <p className="text-xs text-ic-text-tertiary">Loading invitation details...</p>
        </div>
      </InvitationShell>
    );
  }

  // ─── Error / Invalid Token ────────────────────────────────────

  if (invitationQuery.isError || !invitationQuery.data) {
    return (
      <InvitationShell>
        <ErrorCard
          title="Invitation Not Found"
          message="This invitation link is invalid or has expired. Please contact your organization administrator for a new invitation."
        />
      </InvitationShell>
    );
  }

  const invitation = invitationQuery.data;

  // ─── Already Accepted ─────────────────────────────────────────

  if (invitation.status === 'accepted') {
    return (
      <InvitationShell>
        <div className="text-center py-8">
          <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-emerald-500/15">
            <CheckCircle className="h-6 w-6 text-emerald-400" />
          </div>
          <h2 className="text-lg font-semibold text-ic-text-primary">Already Accepted</h2>
          <p className="mt-1 text-xs text-ic-text-tertiary">
            This invitation has already been accepted.
          </p>
          <button
            onClick={() => navigate('/login')}
            className="mt-6 rounded-md bg-ic-accent px-6 py-2 text-xs font-medium text-white hover:bg-ic-accent-hover transition-colors"
          >
            Go to Login
          </button>
        </div>
      </InvitationShell>
    );
  }

  // ─── Expired / Revoked ────────────────────────────────────────

  if (invitation.status === 'expired' || invitation.status === 'revoked') {
    return (
      <InvitationShell>
        <ErrorCard
          title={invitation.status === 'expired' ? 'Invitation Expired' : 'Invitation Revoked'}
          message={
            invitation.status === 'expired'
              ? 'This invitation has expired. Please contact your organization administrator for a new invitation.'
              : 'This invitation has been revoked. Please contact your organization administrator.'
          }
        />
      </InvitationShell>
    );
  }

  // ─── Accepted Successfully ────────────────────────────────────

  if (acceptMutation.isSuccess) {
    return (
      <InvitationShell>
        <div className="text-center py-8">
          <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-emerald-500/15">
            <CheckCircle className="h-6 w-6 text-emerald-400" />
          </div>
          <h2 className="text-lg font-semibold text-ic-text-primary">Invitation Accepted</h2>
          <p className="mt-1 text-xs text-ic-text-tertiary">
            Welcome to {invitation.organization_name}. Redirecting to login...
          </p>
          <div className="mt-4">
            <Loader2 className="mx-auto h-4 w-4 animate-spin text-ic-accent" />
          </div>
        </div>
      </InvitationShell>
    );
  }

  // ─── Pending — Show Invitation Details ────────────────────────

  return (
    <InvitationShell>
      <div className="space-y-6">
        {/* Header */}
        <div className="text-center">
          <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-ic-accent/15">
            <UserPlus className="h-6 w-6 text-ic-accent" />
          </div>
          <h2 className="text-lg font-semibold text-ic-text-primary">You have been invited</h2>
          <p className="mt-1 text-xs text-ic-text-tertiary">
            {invitation.inviter_name} has invited you to join their organization on Impact Core
          </p>
        </div>

        {/* Invitation Details */}
        <div className="space-y-3">
          <DetailRow
            icon={<Building2 className="h-3.5 w-3.5" />}
            label="Organization"
            value={invitation.organization_name}
          />
          <DetailRow
            icon={<Mail className="h-3.5 w-3.5" />}
            label="Invited by"
            value={invitation.inviter_name}
          />
          <DetailRow
            icon={<Shield className="h-3.5 w-3.5" />}
            label="Role"
            value={invitation.role_description ?? invitation.role}
          />
          {invitation.expires_at && (
            <DetailRow
              icon={<Clock className="h-3.5 w-3.5" />}
              label="Expires"
              value={new Date(invitation.expires_at).toLocaleDateString('en-GB', {
                day: '2-digit',
                month: 'short',
                year: 'numeric',
              })}
            />
          )}
        </div>

        {/* Personal Message */}
        {invitation.message && (
          <div className="rounded-md border border-ic-border/50 bg-ic-surface-secondary p-3">
            <p className="mb-1 text-[10px] uppercase tracking-wider text-ic-text-tertiary">
              Personal message
            </p>
            <p className="text-xs text-ic-text-secondary leading-relaxed">
              {invitation.message}
            </p>
          </div>
        )}

        {/* Error */}
        {acceptError && (
          <div className="rounded-md border border-red-500/30 bg-red-500/10 p-3">
            <div className="flex items-center gap-2">
              <AlertCircle className="h-3.5 w-3.5 text-red-400" />
              <span className="text-xs text-red-400">{acceptError}</span>
            </div>
          </div>
        )}

        {/* Accept Button */}
        <button
          onClick={handleAccept}
          disabled={acceptMutation.isPending}
          className="flex w-full items-center justify-center gap-2 rounded-md bg-ic-accent px-4 py-3 text-sm font-semibold text-white hover:bg-ic-accent-hover transition-colors disabled:opacity-50"
        >
          {acceptMutation.isPending ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" />
              Accepting...
            </>
          ) : (
            <>
              <CheckCircle className="h-4 w-4" />
              Accept & Create Account
            </>
          )}
        </button>

        <p className="text-center text-[10px] text-ic-text-tertiary">
          By accepting, you agree to the Impact Core terms of service.
        </p>
      </div>
    </InvitationShell>
  );
}

// ─── Sub-Components ─────────────────────────────────────────────

function InvitationShell({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex min-h-screen items-center justify-center bg-ic-surface p-4">
      <div className="w-full max-w-md rounded-xl border border-ic-border bg-ic-card p-6 shadow-2xl">
        {/* Logo / App Name */}
        <div className="mb-6 text-center">
          <h1 className="text-sm font-bold tracking-wider text-ic-accent">IMPACT CORE</h1>
        </div>
        {children}
      </div>
    </div>
  );
}

function ErrorCard({ title, message }: { title: string; message: string }) {
  return (
    <div className="text-center py-8">
      <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-red-500/15">
        <AlertCircle className="h-6 w-6 text-red-400" />
      </div>
      <h2 className="text-lg font-semibold text-ic-text-primary">{title}</h2>
      <p className="mt-2 text-xs text-ic-text-tertiary leading-relaxed">{message}</p>
    </div>
  );
}

interface DetailRowProps {
  icon: React.ReactNode;
  label: string;
  value: string;
}

function DetailRow({ icon, label, value }: DetailRowProps) {
  return (
    <div className="flex items-center gap-3 rounded-md border border-ic-border/50 bg-ic-surface-secondary px-3 py-2.5">
      <span className="text-ic-text-tertiary">{icon}</span>
      <div className="flex-1">
        <p className="text-[10px] uppercase tracking-wider text-ic-text-tertiary">{label}</p>
        <p className="text-xs font-medium text-ic-text-primary">{value}</p>
      </div>
    </div>
  );
}
